import { useLookup, useWorkspace } from "@/hooks";
import FilterDateRangeSelector from "@/presentation/deduction/components/DeductionFilter/DateRangeSelector";
import { FilterDateRange } from "@/presentation/deduction/components/DeductionFilter/interfaces";
import FilterInfoCol from "@/presentation/deduction/components/DeductionFilter/DeductionFilterInfoCol";
import {
  buildPurchaserOptions,
  buildPromoTypeOptions,
  buildPromoStatusOptions,
  convertDateRangeToDates,
  DATE_CONSTANTS,
} from "@/presentation/deduction/components/DeductionFilter/utils";
import { getEmptyPromotionsFilterInfo, PromotionsFilterInfo } from "@/store/promotion";
import { Button, DatePicker, Division, Flex, Form, Row, Select, Span } from "@ir/ui";
import { useCallback, useEffect, useMemo } from "react";
import { usePromotions } from "../hooks/usePromotions";

interface PromotionsFilterFormProps {
  closeFilter: () => void;
}

const DEFAULT_FILTER_INFO = getEmptyPromotionsFilterInfo();

const PromotionsFilterForm = ({ closeFilter }: PromotionsFilterFormProps) => {
  const [form] = Form.useForm();
  const { settings, workspace } = useWorkspace();
  const {
    getPurchasersLookup,
    purchasersLookup,
    getPromoTypesLookup,
    promoTypesLookup,
    getPromoStatusesLookup,
    promoStatusesLookup,
  } = useLookup();
  const { filterInfo, isFilterApplied, setFilter, setIsFilterApplied, getPromotions, setCurrentPage } = usePromotions();

  useEffect(() => {
    if (!workspace?.workspaceKey) return;
    getPurchasersLookup();
    getPromoTypesLookup();
    getPromoStatusesLookup();
  }, [workspace?.workspaceKey, getPurchasersLookup, getPromoTypesLookup, getPromoStatusesLookup]);

  useEffect(() => {
    form.setFieldsValue({
      purchaser: filterInfo.purchaser,
      promoType: filterInfo.promoType,
      promoStatus: filterInfo.promoStatus,
      startFromDate: filterInfo.startFromDate,
      startToDate: filterInfo.startToDate,
      startDateRange: filterInfo.startDateRange,
    });
  }, [filterInfo, form]);

  const startDateRangeValue = Form.useWatch("startDateRange", form) as FilterDateRange | null;

  const purchaserOptions = useMemo(() => buildPurchaserOptions(purchasersLookup?.data ?? []), [purchasersLookup?.data]);
  const promoTypeOptions = useMemo(() => buildPromoTypeOptions(promoTypesLookup?.data ?? []), [promoTypesLookup?.data]);
  const promoStatusOptions = useMemo(
    () => buildPromoStatusOptions(promoStatusesLookup?.data ?? []),
    [promoStatusesLookup?.data]
  );

  const startFromDateValue = Form.useWatch("startFromDate", form);
  const startToDateValue = Form.useWatch("startToDate", form);
  const startFromDateMaxValue = startToDateValue ?? DATE_CONSTANTS.checkFromDateDefaultMaxValue;
  const startToDateMinValue = startFromDateValue ?? DATE_CONSTANTS.defaultFromDate;

  const onStartDateRangeChange = useCallback(
    (value: FilterDateRange | null) => {
      if (!value) {
        form.setFieldsValue({ startDateRange: null });
        return;
      }
      const { fromDate, toDate } = convertDateRangeToDates(value, 3);
      form.setFieldsValue({
        startFromDate: fromDate,
        startToDate: toDate,
        startDateRange: value,
      });
    },
    [form]
  );

  const onFieldsChange = useCallback(
    (changedValues: Partial<PromotionsFilterInfo>) => {
      if (Object.hasOwn(changedValues, "startFromDate") && startDateRangeValue !== null) {
        form.setFieldsValue({ startDateRange: null });
      }
      if (Object.hasOwn(changedValues, "startToDate") && startDateRangeValue !== null) {
        form.setFieldsValue({ startDateRange: null });
      }
    },
    [form, startDateRangeValue]
  );

  const handleSubmit = useCallback(
    async (values: PromotionsFilterInfo) => {
      const effectiveStartDateRange = values.startDateRange ?? startDateRangeValue;
      let startFromDate = values.startFromDate ?? filterInfo.startFromDate;
      let startToDate = values.startToDate ?? filterInfo.startToDate;

      if (effectiveStartDateRange && (startFromDate == null || startToDate == null)) {
        const derived = convertDateRangeToDates(effectiveStartDateRange, 3);
        startFromDate = startFromDate ?? derived.fromDate;
        startToDate = startToDate ?? derived.toDate;
      }

      setFilter({
        ...filterInfo,
        ...values,
        startFromDate,
        startToDate,
        startDateRange: effectiveStartDateRange,
      });
      setCurrentPage(1);
      setIsFilterApplied(true);
      closeFilter();
      await getPromotions({ isLoading: false, isRefreshing: true });
    },
    [filterInfo, startDateRangeValue, setFilter, setCurrentPage, setIsFilterApplied, closeFilter, getPromotions]
  );

  const handleClear = useCallback(async () => {
    form.resetFields();
    setFilter(getEmptyPromotionsFilterInfo());
    setIsFilterApplied(false);
    setCurrentPage(1);
    await getPromotions({ isLoading: false, isRefreshing: true });
  }, [form, setFilter, setIsFilterApplied, setCurrentPage, getPromotions]);

  return (
    <Form
      form={form}
      className="m-0"
      initialValues={DEFAULT_FILTER_INFO}
      onFinish={handleSubmit}
      onValuesChange={onFieldsChange}
    >
      <Row className="flex-1">
      <FilterInfoCol>
          <Division className="date-range-wrapper">
            <Flex align="center" justifyContent="space-between">
              <Span className="d-flex text-sm font-medium text-grey-700">Start Date:</Span>
              <FilterDateRangeSelector
                value={startDateRangeValue}
                fieldName="checkDateRange"
                onChange={onStartDateRangeChange}
              />
            </Flex>
            <Division className="display-picker mb-3">
              <Division>
                <Form.Item label="From Date" name="startFromDate">
                  <DatePicker
                    showClearButton
                    minValue={DATE_CONSTANTS.checkFromDateDefaultMinValue}
                    maxValue={startFromDateMaxValue}
                    dateFormat={settings?.displayDateFormat}
                    shouldCloseOnInteractOutside
                    isNonModal
                  />
                </Form.Item>
              </Division>
              <Division>
                <Form.Item label="To Date" name="startToDate">
                  <DatePicker
                    showClearButton
                    minValue={startToDateMinValue}
                    maxValue={DATE_CONSTANTS.checkFromDateDefaultMaxValue}
                    dateFormat={settings?.displayDateFormat}
                    shouldCloseOnInteractOutside
                    isNonModal
                  />
                </Form.Item>
              </Division>
            </Division>
          </Division>
        </FilterInfoCol>

        <FilterInfoCol>
          <Form.Item label="Purchaser" name="purchaser">
            <Select
              size="small"
              maxMenuHeight={150}
              isMultiSelect
              isClearable={true}
              isLoading={purchasersLookup?.isLoading}
              defaultValue={filterInfo.purchaser}
              maxTagCount={3}
              maxTagLength={4}
              options={purchaserOptions}
              placeholder="Select"
            />
          </Form.Item>
        </FilterInfoCol>

        <FilterInfoCol>
          <Form.Item label="Promo Type" name="promoType">
            <Select
              size="small"
              maxMenuHeight={150}
              isMultiSelect
              isClearable={true}
              isLoading={promoTypesLookup?.isLoading}
              defaultValue={filterInfo.promoType}
              maxTagCount={3}
              maxTagLength={4}
              options={promoTypeOptions}
              placeholder="Select"
            />
          </Form.Item>
        </FilterInfoCol>

        <FilterInfoCol>
          <Form.Item label="Status" name="promoStatus">
            <Select
              size="small"
              maxMenuHeight={150}
              isMultiSelect
              isClearable={true}
              isLoading={promoStatusesLookup?.isLoading}
              defaultValue={filterInfo.promoStatus}
              maxTagCount={3}
              maxTagLength={4}
              options={promoStatusOptions}
              placeholder="Select"
            />
          </Form.Item>
        </FilterInfoCol>  
      </Row>

      {/* Action Buttons */}
      <Flex justifyContent="space-between" gap={1} className="px-0 py-3">
        <Division>
          {isFilterApplied && (
            <Button
              type="reset"
              variant="clear"
              color="grey"
              size="medium"
              onPress={handleClear}
              isDisabled={!isFilterApplied}
              htmlTitle="Clear filters"
            >
              Clear filters
            </Button>
          )}
        </Division>
        <Division>
          <Button type="button" variant="outline" color="grey" size="medium" onPress={closeFilter} htmlTitle="Close">
            Close
          </Button>
          <Button type="submit" variant="solid" color="primary" size="medium" htmlTitle="Apply">
            Apply
          </Button>
        </Division>
      </Flex>
    </Form>
  );
};

export { PromotionsFilterForm };
